import {
  BorderBox,
  Box,
  Button,
  FlexBox,
  Text,
  TextSmall,
} from '@trmediaab/zebra';
import { Html, Money } from '@trmediaab/zebra-components';
import type { Product } from '@trmediaab/zebra-types/trhub';
import useSWR, { useSWRConfig } from 'swr';

import Card from '../../components/Card';
import ErrorMessage from '../../components/ErrorMessage';

interface ProductCardProps {
  sku: string;
  headline?: string;
  selected: boolean;
  onClick: () => void;
}

const ProductCard = ({
  headline,
  sku,
  selected,
  onClick,
}: ProductCardProps) => {
  const currentKey = `product/${sku}`;
  const { data, error } = useSWR<Product>(currentKey);
  const { cache } = useSWRConfig();

  if (error != null) {
    return <ErrorMessage error={error} />;
  }

  // TODO - loader
  if (!data) {
    return null;
  }

  const {
    price,
    firstPurchaseCampaignPrice,
    headline: productHeadline,
    infoText,
    paymentTerms,
  } = data;

  let savings = 0;

  if ('paymentInterval' in data) {
    const { paymentInterval } = data;

    const yearlyCost = (price / paymentInterval) * 12;

    let mostExpensiveYearlyCost = 0;
    for (const key of cache.keys()) {
      if (key.startsWith('product/') && key !== currentKey) {
        const product = cache.get(key)?.data as Product | undefined;
        if (product != null && 'paymentInterval' in product) {
          mostExpensiveYearlyCost = Math.max(
            mostExpensiveYearlyCost,
            (product.price / product.paymentInterval) * 12,
          );
        }
      }
    }

    savings =
      mostExpensiveYearlyCost > 0 ? mostExpensiveYearlyCost - yearlyCost : 0;
  }

  const hasFirstPurchaseCampaignPrice =
    firstPurchaseCampaignPrice !== null && firstPurchaseCampaignPrice >= 0;

  return (
    <Button
      display="block"
      width="100%"
      height="100%"
      fontWeight="inherit"
      textAlign="left"
      onClick={onClick}
    >
      <Card
        fillHeight
        header={
          headline ? (
            <>
              <BorderBox
                borderRadius="circle"
                border="1"
                bg="white"
                width="16px"
                height="16px"
                mr="2,5"
              >
                {selected && (
                  <BorderBox
                    borderRadius="circle"
                    bg="black"
                    width="10px"
                    height="10px"
                    mt="2px"
                    ml="2px"
                  />
                )}
              </BorderBox>
              <Text fontSize="2" fontWeight="semiBold">
                {headline}
              </Text>
              {savings > 0 && (
                <Box ml="auto" height="100%" py="9px">
                  <FlexBox height="100%" alignItems="center" bg="white" px="2">
                    <TextSmall color="checkout.discount" fontWeight="semiBold">
                      Spara <Money value={savings} />!
                    </TextSmall>
                  </FlexBox>
                </Box>
              )}
            </>
          ) : undefined
        }
        headerVariant={selected ? 'default' : 'muted'}
      >
        <Text
          display="grid"
          lineHeight="medium"
          sx={{ gridTemplateColumns: 'auto auto' }}
        >
          <Text.strong>{productHeadline}</Text.strong>
          <Text.strong pl="2" ml="auto">
            <Money
              value={price}
              fontWeight={hasFirstPurchaseCampaignPrice ? 'semiBold' : 'bold'}
              textDecoration={
                hasFirstPurchaseCampaignPrice ? 'line-through' : undefined
              }
            />
            {hasFirstPurchaseCampaignPrice && (
              <>
                {' '}
                <Money
                  value={firstPurchaseCampaignPrice}
                  color="checkout.discount"
                  fontWeight="extraBold"
                />
              </>
            )}
          </Text.strong>
        </Text>
        <Html mt="2" between="2" variant="checkoutSmall" html={infoText} />
        <TextSmall
          display="block"
          color="text.faded"
          lineHeight="medium"
          mt="2"
        >
          {paymentTerms}
        </TextSmall>
      </Card>
    </Button>
  );
};

export default ProductCard;
